import React, { useEffect, useState } from "react";
import styles from "../css/look-up-order-aftership.module.css";
import InputField from "./input-field";
import { useTranslation } from "react-i18next";
import { SecondaryButtonV2 } from "./secondary-button-v2";
import { useForm } from "react-hook-form";
import { emailPatternRegExp } from "./utils/validations";
import { inputCheckAndGaTracking } from "./utils/look-up-order-utils";
import apiService from "../services/api-service";
import { gtagCommand, dataDogLog } from "../services/monitoring";
import OrderContent from "./order-content";
import { logout } from "../services/auth-client";
import { grabOrder } from "../services/support";
import AftershipIframe from "./aftership-iframe";
import { Loading } from "./loading";
import { useStatusPage } from "./hooks/useStatusPageState";

export const LookUpOrderAftership = (props) => {
  const { t } = useTranslation("lookupOrder");
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setValue,
  } = useForm();
  const [orders, setOrders] = useState([]);
  const [hasGift, setHasGift] = useState(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [isLoading, setIsLoading] = useState(props.hasCredentials);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(props.hasCredentials);
  const [trackingNumberProvided, setTrackingNumberProvided] = useState("");
  const {
    userTypedOrderId,
    setUserTypedOrderId,
    errorFromOrderLookUpService,
    setErrorFromOrderLookUpService,
  } = useStatusPage();
  const [orderId, setOrderId] = useState("");

  const displayOrders = (response) => {
    setOrders(response);
    setValue("orderEmail", response.length && response[0].email);
    if (hasCredentials) {
      setUserTypedOrderId(response.length && response[0].orderId);
    } else {
      setOrderId(response.length && response[0].orderId);
    }
    setHasGift(response.length && response[0].isGift);
    setIsUpgrade(response.length && response[0].isUpgrade);
    setIsLoading(false);
  };

  const resetValues = () => {
    logout();
    setOrders([]);
    setErrorFromOrderLookUpService();
    setIsLoading(true);
    setIsLoggedIn(false);
    setHasCredentials(false);
    setTrackingNumberProvided("");
  };

  const orderError = (err, obj) => {
    console.warn(err);
    setErrorFromOrderLookUpService(err);
    setIsLoading(false);
    dataDogLog("orderLookUp", obj);
  };

  const handleEnterKey = (e) => {
    const { orderZip, orderEmail } = watch();

    const values = {
      orderId: orderId,
      orderZip: orderZip,
      orderEmail: orderEmail,
    };

    if (e.key === "Enter" && !isDisabled) {
      lookUpOrder(values);
    }
  };

  const onSubmit = (data) => {
    if (data.trackingNumber) {
      setIsLoading(true);
      setTrackingNumberProvided(data.trackingNumber);
    } else {
      lookupOrder(data);
    }
  };

  const lookupOrder = (values) => {
    resetValues();
    if (!values.orderEmail) {
      values.orderEmail = watch("orderEmail");
      values.isRegionWithNoZipCode = false;
    }
    inputCheckAndGaTracking(values);

    apiService.getOrderLookUp(values).then(
      (result) => {
        displayOrders(result);
        gtagCommand("Order-Status", "confirm", "Order Look Up - Complete");
      },
      (error) => {
        orderError(error.message, values);
      }
    );
  };

  useEffect(() => {
    if (props?.data?.orderId && props?.data?.orderEmail) {
      setUserTypedOrderId(props.data.orderId);
      lookupOrder(props.data);
    }
  }, [props?.data?.orderId, props?.data?.orderEmail]);

  useEffect(() => {
    // note: this mimics the following pre existing logic at look-up-order.js https://github.com/WhoopInc/shipping-status/blob/c2eabe00d89c42c86df336715ce4563733a9f2c2/client/src/components/look-up-order.js#L144-L156
    if (hasCredentials && !isLoggedIn) {
      const data = {
        orderId: null,
        orderEmail: null,
        zip: null,
      };
      setIsLoggedIn(true);
      setIsLoading(true);
      grabOrder(data, displayOrders, orderError);
    }
  }, [hasCredentials, isLoggedIn]);

  if (trackingNumberProvided) {
    return (
      <div className={styles.shippingContainer}>
        <div className={styles.card}>
          <div className={styles.shippingInfo}>
            <AftershipIframe trackingNumber={trackingNumberProvided} />
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div className={styles.container}>
      {orders.length > 0 ? (
        orders.map((item, index) => {
          return (
            <OrderContent
              key={index}
              data={item}
              displayOrders={displayOrders}
              resetValues={() => {}}
              trackingNumber={watch("trackingNumber")}
              email={watch("orderEmail")}
              userTypedOrderId={userTypedOrderId || watch("orderId")}
            />
          );
        })
      ) : (
        <div className={styles.card}>
          <div className={styles.headingText}>
            <h1>{t("AftershipExperimentTitle")}</h1>
            <span>{t("subTitle")}</span>
          </div>
          <form
            className={styles.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            id="orderStatusForm"
          >
            <div className={styles.inputAndLabelContainer}>
              <div>
                <label className={styles.label}>
                  {t("orderNumberAndEmailLabel")}
                </label>
              </div>

              <div className={styles.inputFieldsContainer}>
                <div className={styles.inputAndErrorContainer}>
                  <InputField
                    type="text"
                    name="orderId"
                    placeholder={t("orderNumberPlaceholder")}
                    testId="order-id"
                    {...register("orderId", {
                      required:
                        !watch("trackingNumber") && t("orderNumberError"),
                    })}
                  />
                  {errors?.orderId?.message && (
                    <span className={styles.errorMessage}>
                      {errors?.orderId?.message}
                    </span>
                  )}
                </div>
                <div className={styles.inputAndErrorContainer}>
                  <InputField
                    type="text"
                    name="orderEmail"
                    placeholder={t("emailAddressPlaceholder")}
                    testId="order-email"
                    {...register("orderEmail", {
                      required: !watch("trackingNumber") && t("emailRequired"),
                      ...(watch("trackingNumber")
                        ? {}
                        : {
                            pattern: {
                              value: emailPatternRegExp,
                              message: t("emailAddressError"),
                            },
                          }),
                    })}
                    handleEnterKey={handleEnterKey}
                  />
                  {errors?.orderEmail?.message && (
                    <span className={styles.errorMessage}>
                      {errors?.orderEmail?.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={`${styles.dividerContainer}`}>
              <hr className={styles.divider} />
              <span className={styles.text}>{t("or")}</span>
              <hr className={styles.divider} />
            </div>
            <div className={styles.inputAndLabelContainer}>
              <label className={styles.label}>{t("trackingNumberLabel")}</label>
              <div className={styles.inputAndErrorContainer}>
                <InputField
                  type="text"
                  name="trackingNumber"
                  placeholder={t("trackingNumberPlaceholder")}
                  {...register("trackingNumber", {
                    pattern: {
                      value: /^[0-9a-zA-Z-]+$/,
                      message: t("trackingNumberError"),
                    },
                  })}
                />
                {errors?.trackingNumber?.message && (
                  <span className={styles.errorMessage}>
                    {errors?.trackingNumber?.message}
                  </span>
                )}
              </div>
            </div>
          </form>

          <div className={styles.buttonAndErrorContainer}>
            <SecondaryButtonV2
              variant="survey"
              type="submit"
              form="orderStatusForm"
              data-test-id="look-up-order"
            >
              {t("checkOrderStatusButton")}
            </SecondaryButtonV2>

            {errorFromOrderLookUpService && (
              <span className={styles.errorMessage}>
                {errorFromOrderLookUpService}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
